import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { apiClient, VerifyEmailRequest } from '../api/client';
import { AlertCircle, CheckCircle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ModeToggle } from '@/components/mode-toggle';
import { DynamicLogo } from '@/components/DynamicLogo';

export function VerifyEmail() {
  const navigate = useNavigate();
  const location = useLocation();

  // Get email, userId, message, and invitation status from the previous step
  const { email, userId, isInvited, message } = location.state || {};
  
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState(message || '');
  const [isResending, setIsResending] = useState(false);

  // If no email, redirect back to register (userId is optional for login flow)
  React.useEffect(() => {
    if (!email) {
      navigate('/register');
    }
  }, [email, navigate]);

  const validateCode = (inputCode: string): boolean => {
    return inputCode.length === 6 && /^[A-Z0-9]+$/.test(inputCode.toUpperCase());
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateCode(code)) {
      setError('Please enter a valid 6-character verification code');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const verifyData: VerifyEmailRequest = {
        code: code.toUpperCase(),
      };
      
      // Add timeout to prevent hanging requests
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timeout')), 10000)
      );
      
      const response = await Promise.race([
        apiClient.verifyEmail(verifyData),
        timeoutPromise
      ]) as any;
      
      setSuccessMessage('Email verified successfully!');

      const loggedIn = !!response.session_token;

      // Redirect based on whether we have userId (from register) or not (from login)
      setTimeout(() => {
        if (!userId) {
          // Coming from login flow
          if (loggedIn) {
            navigate('/home');
          } else {
            navigate('/login', {
              state: {
                email: email,
                message: 'Email verified successfully! You can now log in.'
              }
            });
          }
        } else if (isInvited) {
          // Invited users already belong to an organization
          if (loggedIn) {
            navigate('/home');
          } else {
            // 2FA-enabled accounts still need to log in normally
            navigate('/login', {
              state: {
                email: email,
                message: 'Email verified successfully! You can now log in.'
              }
            });
          }
        } else {
          // New users go straight to the setup wizard, which opens with the
          // org-naming step (the tenant was created with an auto-generated
          // placeholder name during verification).
          if (loggedIn) {
            navigate('/setup');
          } else {
            // 2FA-enabled accounts still need to log in normally
            navigate('/login', {
              state: {
                email: email,
                message: 'Email verified successfully! You can now log in.'
              }
            });
          }
        }
      }, 1500);
    } catch (error: any) {
      console.error('Email verification error:', error);
      if (error.message === 'Request timeout') {
        setError('Request timed out. Please check your connection and try again.');
      } else if (error.response?.status === 404) {
        setError('Service temporarily unavailable. Please try again later.');
      } else {
        setError(error.response?.data || 'Invalid verification code. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    setIsResending(true);
    setError('');

    try {
      // Add timeout to prevent hanging requests
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timeout')), 10000)
      );
      
      await Promise.race([
        apiClient.resendVerificationEmail({ email }),
        timeoutPromise
      ]);
      
      setSuccessMessage('New verification code sent! Please check your email.');
      setError('');
    } catch (error: any) {
      console.error('Resend verification error:', error);
      if (error.message === 'Request timeout') {
        setError('Request timed out. Please check your connection and try again.');
      } else if (error.response?.status === 404) {
        setError('Service temporarily unavailable. Please try again later.');
      } else {
        setError('Failed to resend verification code. Please try again.');
      }
    } finally {
      setIsResending(false);
    }
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase().slice(0, 6);
    setCode(value);
    if (error) setError('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-50 via-white to-neutral-100 dark:bg-background dark:from-transparent dark:via-transparent dark:to-transparent flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      {/* Theme toggle */}
      <div className="absolute top-6 right-6">
        <ModeToggle />
      </div>

      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <DynamicLogo size="xl" className="drop-shadow-lg" />
        </div>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-foreground">
          Verify your email
        </h2>
        <p className="mt-2 text-center text-lg text-muted-foreground">
          We've sent a 6-character code to {email}
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <Card>
          <CardHeader>
            <CardTitle>Enter verification code</CardTitle>
            <CardDescription>
              Check your email and enter the 6-character verification code below
            </CardDescription>
          </CardHeader>
          <CardContent>
            {successMessage && (
              <Alert className="mb-6">
                <CheckCircle className="h-4 w-4" />
                <AlertDescription>
                  {successMessage}
                </AlertDescription>
              </Alert>
            )}

            {error && (
              <Alert variant="destructive" className="mb-6">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <form className="space-y-6" onSubmit={handleSubmit}>
              <div className="space-y-2">
                <Label htmlFor="code">Verification Code</Label>
                <Input
                  id="code"
                  name="code"
                  type="text"
                  required
                  value={code}
                  onChange={handleCodeChange}
                  placeholder="ABC123"
                  className={`text-center text-2xl tracking-wider ${error ? "border-destructive" : ""}`}
                  maxLength={6}
                  autoComplete="one-time-code"
                />
                <p className="text-xs text-muted-foreground text-center">
                  Enter the 6-character code from your email
                </p>
              </div>

              <Button 
                type="submit" 
                className="w-full" 
                disabled={isLoading || code.length !== 6}
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                    Verifying...
                  </>
                ) : (
                  'Verify Email'
                )}
              </Button>
            </form>

            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-background text-muted-foreground">Didn't receive the code?</span>
                </div>
              </div>

              <div className="mt-4 text-center">
                <Button
                  variant="outline"
                  onClick={handleResendCode}
                  disabled={isResending}
                  className="text-sm"
                >
                  {isResending ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Resend code
                    </>
                  )}
                </Button>
              </div>

              <div className="mt-4 text-center text-sm text-muted-foreground">
                Wrong email?{' '}
                <Link to="/register" className="font-medium text-primary hover:text-primary/80">
                  Start over
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}